/**
 * @fileoverview Interactive template preview mode for the system-web template canvas.
 * @module system-web/template-builder
 *
 * Responsibilities:
 * - Render an interactive, one-form-at-a-time preview of a system inspection template.
 * - Display header form rich text content via a TipTap read-only editor.
 * - Render the Contacts module with full add/edit/delete interactivity.
 * - Render violations module with configuration summary and manual entry.
 * - Render standard forms with simplified but interactive field inputs.
 * - Collect session mock-data and display it in a resizable Session Data panel.
 *
 * Dependencies:
 * - ContactsModulePreview, HeaderFormRenderer from @firesquirrel/ui-admin (shared)
 * - @firesquirrel/core-domain for rule evaluation and blank value initialisation
 * - lucide-react for icons
 *
 * Architecture:
 * - Presentation component; receives `template` and `forms` from the canvas host.
 * - No direct API calls; no Dexie reads. Pure preview with local state.
 * - Satisfies the `previewRenderer` contract:
 *     React.ComponentType<{ template: TemplateBuilderTemplate; forms: Map<string, TemplateBuilderForm> }>
 *
 * Notes:
 * - This component replaces the old static `SystemTemplatePreview` which showed
 *   only placeholder text for header and module forms with no interactivity.
 * - Header form content is rendered via TipTap with `editable: false` to sanitise
 *   output consistently with how the content was authored.
 * - For violations modules, a simplified log-entry UI is provided so system admins
 *   can verify the configuration without needing a live code-set search backend.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Smartphone, Tablet, Monitor, ClipboardList,
  X as XIcon, AlertTriangle, Trash2, Plus, EyeOff,
} from 'lucide-react';
import { ContactsModulePreview, HeaderFormRenderer } from '@firesquirrel/ui-admin';
import type { ContactChange } from '@firesquirrel/ui-admin';
import type { TemplateBuilderTemplate, TemplateBuilderForm, FormField, ViolationsModuleConfig, ContactsModuleConfig } from '@firesquirrel/ui-admin';
import {
  evaluateTemplateRules,
  buildBlankValues,
  buildPreviewTelemetry,
  type AllFormValues,
  type InspectionForm,
  type InspectionRule,
  type ContactEntry,
} from '@firesquirrel/core-domain';
// Phase 7 cutover — canonical unified inspection renderer (B18 fix).
import {
  InspectionRenderer,
  DataSlideIn,
  createSessionAdapter,
  resolveFieldDefault,
  type PersistAdapter,
} from '@firesquirrel/inspection-renderer';
import {
  evaluateAllFormRulesWithTrace,
  type ResolvedFormStateV2,
  type RuleTraceEntry,
  type FieldResponse,
} from '@firesquirrel/core-domain';
import { buildPreviewAttachmentPicker, buildPreviewResolveContext } from '../../lib/buildPreviewAdapters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DeviceWidth = 'phone' | 'tablet' | 'desktop';

const DEVICE_WIDTHS: Record<DeviceWidth, number> = {
  phone: 390,
  tablet: 768,
  desktop: 1024,
};

/**
 * @contract LoggedViolationEntry
 *
 * Purpose:
 * - Represents a manually entered violation record in the system-web preview.
 *
 * Fields:
 * - id: Unique preview entry id.
 * - code: User-entered code string.
 * - description: User-entered description.
 * - note: Optional inspector note.
 * - severity: Chosen severity level.
 *
 * Sync:
 * - Preview-only; not persisted to Dexie.
 */
interface LoggedViolationEntry {
  id: string;
  code: string;
  description: string;
  note?: string;
  severity: 'normal' | 'high' | 'critical';
}

// ---------------------------------------------------------------------------
// Session Data panel width persistence
// ---------------------------------------------------------------------------
const SESSION_PANEL_STORAGE_KEY = 'firesquirrel.systemPreview.sessionPanelWidth';
const SESSION_PANEL_MIN_WIDTH = 224;
const SESSION_PANEL_FORM_MIN_RESERVE = 320;

function readStoredPanelWidth(): number {
  if (typeof window === 'undefined') return SESSION_PANEL_MIN_WIDTH;
  try {
    const raw = window.localStorage.getItem(SESSION_PANEL_STORAGE_KEY);
    if (!raw) return SESSION_PANEL_MIN_WIDTH;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < SESSION_PANEL_MIN_WIDTH) return SESSION_PANEL_MIN_WIDTH;
    return n;
  } catch {
    return SESSION_PANEL_MIN_WIDTH;
  }
}

// ---------------------------------------------------------------------------
// ViolationsModuleRenderer — simplified violations entry for system admin preview
// ---------------------------------------------------------------------------

/**
 * @component ViolationsModuleRenderer
 *
 * Purpose:
 * - Provides a simplified violations entry UI for system admin preview.
 * - System admins are verifying configuration, not doing live code-set lookups.
 *
 * Props:
 * - config: ViolationsModuleConfig for this form.
 * - onViolationsChange: Callback fired with the current violation list on each change.
 *
 * Behavior:
 * - Shows a configuration summary at the top so admins can verify flags.
 * - Allows manual entry of a sample violation (code + description + optional note).
 * - Supports severity picker when config.showSeverity is true.
 * - Supports notes field when config.showNotes is true.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No live code-set search (system-web code-set service not injected here).
 *
 * Performance:
 * - Local state only.
 */
function ViolationsModuleRenderer({
  config,
  onViolationsChange,
}: {
  config: ViolationsModuleConfig;
  onViolationsChange?: (violations: LoggedViolationEntry[]) => void;
}) {
  const [violations, setViolations] = useState<LoggedViolationEntry[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ code: '', description: '', note: '', severity: 'normal' as LoggedViolationEntry['severity'] });

  const handleAdd = () => {
    if (!draft.code.trim()) return;
    const entry: LoggedViolationEntry = {
      id: crypto.randomUUID(),
      code: draft.code.trim(),
      description: draft.description.trim(),
      note: config.showNotes && draft.note.trim() ? draft.note.trim() : undefined,
      severity: config.showSeverity ? draft.severity : 'normal',
    };
    const next = [...violations, entry];
    setViolations(next);
    onViolationsChange?.(next);
    setDraft({ code: '', description: '', note: '', severity: 'normal' });
    setAddOpen(false);
  };

  const handleDelete = (id: string) => {
    const next = violations.filter((v) => v.id !== id);
    setViolations(next);
    onViolationsChange?.(next);
  };

  return (
    <div className="bg-gray-50">
      {/* Configuration summary */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={14} className="text-orange-500" />
          <span className="font-semibold text-gray-900 text-[15px]">Violations</span>
          {violations.length > 0 && (
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">
              {violations.length}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          {config.showNotes && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Notes {config.requireNotes ? '(required)' : ''}</span>}
          {config.showSeverity && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Severity picker</span>}
          {config.showPhotos && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Photos {config.requirePhotos ? '(required)' : ''}</span>}
          {config.allowAiSearch && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">AI search</span>}
          {config.codeSetIds.length > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
              {config.codeSetIds.length} code set{config.codeSetIds.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Logged violations list */}
      <div className="px-3 py-2 space-y-2">
        {violations.length === 0 && !addOpen && (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mb-3">
              <AlertTriangle size={22} className="text-orange-300" />
            </div>
            <p className="text-[14px] font-semibold text-gray-600 mb-1">No violations logged</p>
            <p className="text-xs text-gray-400">Use the button below to add a sample violation entry.</p>
          </div>
        )}
        {violations.map((v) => (
          <div key={v.id} className="bg-white rounded-xl border border-gray-200 px-3 py-2.5 flex items-start gap-2.5 shadow-sm">
            <AlertTriangle size={13} className={`mt-0.5 shrink-0 ${v.severity === 'critical' ? 'text-red-500' : v.severity === 'high' ? 'text-amber-500' : 'text-orange-400'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-gray-800">{v.code}</span>
                {v.severity !== 'normal' && (
                  <span className={`text-[9px] px-1 py-0.5 rounded font-semibold uppercase ${v.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {v.severity}
                  </span>
                )}
              </div>
              {v.description && <p className="text-xs text-gray-600 mt-0.5">{v.description}</p>}
              {v.note && <p className="text-[11px] text-gray-400 mt-0.5 italic">"{v.note}"</p>}
            </div>
            <button
              onClick={() => handleDelete(v.id)}
              className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Remove violation"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      {/* Add violation form */}
      {addOpen ? (
        <div className="mx-3 mb-3 bg-white rounded-xl border border-orange-200 shadow-sm p-3 space-y-2.5">
          <p className="text-xs font-semibold text-gray-700 mb-1">Add Sample Violation</p>
          <div>
            <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Code *</label>
            <input
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
              placeholder="e.g. 903.2.1.1"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</label>
            <input
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="Violation description"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>
          {config.showNotes && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Note {config.requireNotes && <span className="text-red-400 normal-case">(required)</span>}
              </label>
              <textarea
                value={draft.note}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                placeholder="Inspector observation…"
                rows={2}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-orange-400 transition resize-none"
              />
            </div>
          )}
          {config.showSeverity && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Severity</label>
              <div className="flex gap-2">
                {(['normal', 'high', 'critical'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, severity: s }))}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors capitalize ${
                      draft.severity === s
                        ? s === 'critical' ? 'bg-red-600 border-red-600 text-white' : s === 'high' ? 'bg-amber-500 border-amber-500 text-white' : 'bg-gray-600 border-gray-600 text-white'
                        : 'border-gray-200 text-gray-600 bg-white hover:border-gray-300'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleAdd}
              disabled={!draft.code.trim() || (config.requireNotes && !draft.note.trim())}
              className="flex-1 py-1.5 text-xs font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-40 transition-colors"
            >
              Log Violation
            </button>
            <button
              onClick={() => { setAddOpen(false); setDraft({ code: '', description: '', note: '', severity: 'normal' }); }}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="px-3 pb-3">
          <button
            onClick={() => setAddOpen(true)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-orange-600 border border-dashed border-orange-300 rounded-xl hover:bg-orange-50 transition-colors"
          >
            <Plus size={13} /> Add Sample Violation
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleFieldPreview — renders individual form fields with basic interactivity
// ---------------------------------------------------------------------------

/**
 * @component SimpleFieldPreview
 *
 * Purpose:
 * - Renders a single form field with a basic interactive input for mock-data entry.
 * - Covers common field types; unsupported types fall back to a labelled placeholder.
 *
 * Props:
 * - field: FormField definition from the template.
 * - value: Current mock value.
 * - onChange: Callback to update the value in parent state.
 * - hidden: Whether this field is currently hidden by a rule.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No API calls. Media fields (photo, signature, camera) show a read-only placeholder.
 *
 * Performance:
 * - Uncontrolled inputs with controlled value fallback; no memoization needed.
 */
function SimpleFieldPreview({
  field,
  value,
  onChange,
  hidden,
}: {
  field: FormField;
  value: any;
  onChange: (v: any) => void;
  hidden: boolean;
}) {
  if (hidden) return null;

  const inputClass = 'w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-orange-400 transition';
  const labelEl = (
    <label className="block text-[11px] font-medium text-gray-600 mb-1">
      {field.label || <span className="italic text-gray-400">Untitled field</span>}
      {field.required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  );

  switch (field.type) {
    case 'section':
      return (
        <div className="col-span-full pt-2 pb-1 border-b border-gray-200">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            {field.label || 'Section'}
          </span>
        </div>
      );

    case 'instructions':
      return (
        <div className="col-span-full px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-100">
          <p className="text-xs text-blue-700">{field.label || 'Instruction text'}</p>
        </div>
      );

    case 'toggle': {
      const checked = Boolean(value);
      return (
        <div className="flex items-center gap-2">
          {labelEl}
          <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`shrink-0 w-10 h-5.5 rounded-full transition-colors relative ${checked ? 'bg-orange-500' : 'bg-gray-300'}`}
            style={{ minWidth: 40, height: 22 }}
          >
            <span
              className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
              style={{ left: checked ? 20 : 2 }}
            />
          </button>
        </div>
      );
    }

    case 'text':
      return (
        <div>
          {labelEl}
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.extras?.placeholder || ''}
            className={inputClass}
          />
        </div>
      );

    case 'textarea':
      return (
        <div className="col-span-full">
          {labelEl}
          <textarea
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.extras?.placeholder || ''}
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </div>
      );

    case 'number':
    case 'measurement':
      return (
        <div>
          {labelEl}
          <div className="flex gap-1">
            <input
              type="number"
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="0"
              className={`${inputClass} flex-1`}
            />
            {field.type === 'measurement' && field.extras?.unit && (
              <span className="inline-flex items-center px-2.5 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-lg">
                {field.extras.unit}
              </span>
            )}
          </div>
        </div>
      );

    case 'date':
      return (
        <div>
          {labelEl}
          <input
            type="date"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        </div>
      );

    case 'dateTime':
      return (
        <div>
          {labelEl}
          <input
            type="datetime-local"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        </div>
      );

    case 'dropdown': {
      const opts: Array<{ label: string; value: string }> = (field.extras?.options as Array<{ label: string; value: string }> | undefined) ?? [];
      return (
        <div>
          {labelEl}
          <select
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          >
            <option value="">— Select —</option>
            {opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      );
    }

    case 'radioGroup': {
      const opts: Array<{ label: string; value: string }> = (field.extras?.options as Array<{ label: string; value: string }> | undefined) ?? [];
      return (
        <div className="col-span-full">
          {labelEl}
          <div className="flex flex-wrap gap-2 mt-1">
            {opts.map((o) => (
              <label key={o.value} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name={field.id}
                  value={o.value}
                  checked={value === o.value}
                  onChange={() => onChange(o.value)}
                  className="accent-orange-500"
                />
                <span className="text-sm text-gray-700">{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    case 'checkboxGroup': {
      const opts: Array<{ label: string; value: string }> = (field.extras?.options as Array<{ label: string; value: string }> | undefined) ?? [];
      const selected: string[] = Array.isArray(value) ? value : [];
      return (
        <div className="col-span-full">
          {labelEl}
          <div className="flex flex-col gap-1.5 mt-1">
            {opts.map((o) => (
              <label key={o.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={() => {
                    const next = selected.includes(o.value)
                      ? selected.filter((v) => v !== o.value)
                      : [...selected, o.value];
                    onChange(next);
                  }}
                  className="accent-orange-500"
                />
                <span className="text-sm text-gray-700">{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    case 'photo':
    case 'camera':
    case 'signature':
      return (
        <div>
          {labelEl}
          <div className="h-10 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center">
            <span className="text-[10px] text-gray-400 italic">
              {field.type === 'signature' ? 'Signature capture' : 'Photo/camera — not available in preview'}
            </span>
          </div>
        </div>
      );

    case 'location':
      return (
        <div className="col-span-full">
          {labelEl}
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter address or GPS coordinates"
            className={inputClass}
          />
        </div>
      );

    case 'dataBound':
      return (
        <div>
          {labelEl}
          <div className="h-8 rounded-md border border-gray-200 bg-blue-50 px-2.5 flex items-center">
            <span className="text-[10px] text-blue-500">
              Data-bound field — resolved from inspection context
            </span>
          </div>
        </div>
      );

    case 'repeatableList':
      return (
        <div className="col-span-full">
          {labelEl}
          <div className="h-10 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center">
            <span className="text-[10px] text-gray-400 italic">Repeatable list — interactive in inspection app</span>
          </div>
        </div>
      );

    default:
      return (
        <div>
          {labelEl}
          <div className="h-8 rounded-md border border-gray-200 bg-gray-50 px-2.5 flex items-center">
            <span className="text-[10px] text-gray-400">{field.type}</span>
          </div>
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// StandardFormRenderer — renders a non-module, non-header form
// ---------------------------------------------------------------------------

/**
 * @component StandardFormRenderer
 *
 * Purpose:
 * - Renders all sections and fields of a standard form with interactive inputs.
 *
 * Props:
 * - form: The form definition.
 * - values: Current field value map for this form.
 * - onValuesChange: Callback to update the field values.
 * - hiddenFields: Set of field IDs hidden by active rules.
 * - hiddenSections: Set of section IDs hidden by active rules.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No API calls. Rule evaluation is done by parent.
 *
 * Performance:
 * - Renders synchronously; no deferred sections.
 */
function StandardFormRenderer({
  form,
  values,
  onValuesChange,
  hiddenFields,
  hiddenSections,
}: {
  form: TemplateBuilderForm;
  values: Record<string, any>;
  onValuesChange: (next: Record<string, any>) => void;
  hiddenFields: Set<string>;
  hiddenSections: Set<string>;
}) {
  const sections = [...(form.layout?.sections ?? [])].sort((a, b) => a.order - b.order);
  const fieldMap = new Map<string, FormField>(form.fields.map((f) => [f.id, f]));

  const handleChange = useCallback((fieldId: string, v: any) => {
    onValuesChange({ ...values, [fieldId]: v });
  }, [values, onValuesChange]);

  return (
    <div className="w-full">
      <div className="px-4 py-3 bg-gray-50 border-b">
        <h2 className="text-base font-semibold text-gray-900">{form.name}</h2>
        {form.description && !form.formKind && (
          <p className="text-xs text-gray-500 mt-0.5">{form.description}</p>
        )}
      </div>
      <div className="p-4 space-y-6">
        {sections.length === 0 && (
          <p className="text-xs text-gray-400 italic text-center py-4">No sections in this form yet.</p>
        )}
        {sections.map((section) => {
          if (hiddenSections.has(section.id)) {
            return (
              <div key={section.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/60">
                <EyeOff size={12} className="text-amber-500 shrink-0" />
                <span className="text-xs font-medium text-amber-700">{section.title || 'Untitled Section'}</span>
                <span className="text-[10px] text-amber-500">— HIDDEN by rule</span>
              </div>
            );
          }

          const sectionFields = (section.fieldIds ?? [])
            .map((fid) => fieldMap.get(fid))
            .filter(Boolean) as FormField[];

          const effectiveCols = section.columns ?? 1;
          const gridClass =
            effectiveCols === 3 ? 'grid grid-cols-3 gap-3' :
            effectiveCols === 2 ? 'grid grid-cols-2 gap-3' :
            'space-y-4';

          return (
            <div key={section.id} className="space-y-3">
              {section.title && (
                <div className="pb-1 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">{section.title}</h3>
                  {section.description && (
                    <p className="text-xs text-gray-400 mt-0.5">{section.description}</p>
                  )}
                </div>
              )}
              <div className={gridClass}>
                {sectionFields.length === 0 && (
                  <p className="text-[10px] text-gray-400 italic py-1 col-span-full">No fields in this section.</p>
                )}
                {sectionFields.map((field) => (
                  <SimpleFieldPreview
                    key={field.id}
                    field={field}
                    value={values[field.id]}
                    onChange={(v) => handleChange(field.id, v)}
                    hidden={hiddenFields.has(field.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SystemTemplatePreviewMode — main exported component
// ---------------------------------------------------------------------------

/**
 * @component SystemTemplatePreviewMode
 *
 * Purpose:
 * - Full interactive template preview for the system-web template canvas.
 * - Replaces the previous static `SystemTemplatePreview` which only showed
 *   placeholder text for header and module forms.
 *
 * Props:
 * - template: The system inspection template being previewed.
 * - forms: Map from formId to TemplateBuilderForm for all forms in the template.
 *
 * Behavior:
 * - Navigates through forms one at a time with prev/next and dot controls.
 * - Header forms render rich-text HTML via TipTap read-only editor.
 * - Contact module forms render the full ContactsModulePreview with interactivity.
 * - Violations module forms render a simplified entry UI.
 * - Standard forms render interactive field inputs.
 * - Session Data panel on the right tracks all entered mock data.
 * - Template rules are evaluated and respected (hidden forms/sections/fields).
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No API calls, no Dexie access. Preview-only; all data is local state.
 *
 * Performance:
 * - Rule evaluation runs on each value change; memoized inside core-domain functions.
 */
export function SystemTemplatePreviewMode({ template, forms }: {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
}) {
  // ── Phase 7 cutover — canonical PersistAdapter + host adapters (B18 fix). ──
  //
  // Derive initial field defaults from every form in the template and pass
  // them as `initialFormValues` so every widget type (Toggle, CheckboxGroup,
  // RadioGroup, Dropdown, etc.) renders at its configured `defaultValue`
  // without requiring user interaction. Sentinel strings ('today', 'now') are
  // excluded — DateField / DateTimeField resolve those at widget mount.
  //
  // Both the initial values and the adapter are intentionally captured from
  // the `forms` prop at mount (empty deps). The adapter is session-scoped and
  // must not be recreated on prop changes; `reset()` restores these defaults.
  const persistAdapter: PersistAdapter = useMemo(() => {
    const initialFormValues: Record<string, Record<string, FieldResponse>> = {};
    for (const [formId, form] of forms) {
      for (const field of form.fields) {
        // resolveFieldDefault reads field.defaultValue OR extras.defaultState
        // / defaultToToday / defaultToNow (template-builder UI conventions).
        const dv = resolveFieldDefault(field as unknown as import('@firesquirrel/core-domain').InspectionFormField);
        if (dv == null || dv === 'today' || dv === 'now') continue;
        if (!initialFormValues[formId]) initialFormValues[formId] = {};
        initialFormValues[formId][field.id] = { value: dv };
      }
    }
    return createSessionAdapter({ actorId: 'preview-inspector', initialFormValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const attachmentPicker = useMemo(() => buildPreviewAttachmentPicker(), []);
  const resolveContext = useMemo(() => buildPreviewResolveContext(), []);
  // Stable `openedAt` for the Data slide-in (lives across renders).
  const openedAtRef = useRef<string>(new Date().toISOString());

  // Subscribe to PersistAdapter notifies so the form-nav sidebar telemetry
  // (hidden forms, completion %, etc.) re-renders alongside the embedded
  // renderer's internal state.
  const [, setAdapterTick] = useState(0);
  useEffect(() => persistAdapter.subscribe(() => setAdapterTick((t) => t + 1)), [persistAdapter]);

  // Mirror the canonical responses into the legacy `allValues` shape so the
  // existing toolbar telemetry (rule evaluation, hidden-form chips, form nav
  // collapsing) keeps working without a rewrite.
  const allValues: AllFormValues = useMemo(() => {
    const out: AllFormValues = buildBlankValues(forms as unknown as ReadonlyMap<string, InspectionForm>);
    const canonical = persistAdapter.getCanonicalResponses();
    for (const fid of Object.keys(out)) {
      for (const fieldId of Object.keys(out[fid])) {
        const r = canonical[fieldId];
        if (r) out[fid][fieldId] = r.value as never;
      }
    }
    return out;
    // Re-derive on adapter notify.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms, persistAdapter]);

  const [contactChanges, setContactChanges] = useState<Record<string, ContactChange[]>>({});
  const [violationChanges, setViolationChanges] = useState<Record<string, any[]>>({});
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [device, setDevice] = useState<DeviceWidth>('desktop');
  const [showHiddenForms, setShowHiddenForms] = useState(false);
  const [dataPanel, setDataPanel] = useState(true);
  const layoutContainerRef = useRef<HTMLDivElement | null>(null);
  const [dataPanelWidth, setDataPanelWidth] = useState<number>(() => readStoredPanelWidth());
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isResizingRef = useRef(false);

  // ── Panel width persistence ──
  useEffect(() => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      try { window.localStorage.setItem(SESSION_PANEL_STORAGE_KEY, String(dataPanelWidth)); } catch { /* quota */ }
    }, 100);
    return () => { if (persistTimerRef.current) clearTimeout(persistTimerRef.current); };
  }, [dataPanelWidth]);

  const getMaxPanelWidth = useCallback(() => {
    const el = layoutContainerRef.current;
    if (!el) return SESSION_PANEL_MIN_WIDTH;
    return Math.max(SESSION_PANEL_MIN_WIDTH, el.getBoundingClientRect().width - SESSION_PANEL_FORM_MIN_RESERVE);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      setDataPanelWidth((w) => Math.min(Math.max(w, SESSION_PANEL_MIN_WIDTH), getMaxPanelWidth()));
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [getMaxPanelWidth]);

  const handleResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const container = layoutContainerRef.current;
    if (!container) return;
    isResizingRef.current = true;
    let raf = 0;
    const onMove = (ev: PointerEvent) => {
      if (!isResizingRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const next = rect.right - ev.clientX;
        setDataPanelWidth(Math.min(Math.max(next, SESSION_PANEL_MIN_WIDTH), getMaxPanelWidth()));
      });
    };
    const onUp = () => {
      isResizingRef.current = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [getMaxPanelWidth]);

  const handleResizeKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = e.shiftKey ? 64 : 16;
    const delta = e.key === 'ArrowLeft' ? step : -step;
    setDataPanelWidth((w) => Math.min(Math.max(w + delta, SESSION_PANEL_MIN_WIDTH), getMaxPanelWidth()));
  }, [getMaxPanelWidth]);

  // ── Rule evaluation ──
  const ruleResult = evaluateTemplateRules(
    (template.rules || []) as unknown as InspectionRule[],
    allValues,
  );

  // ── Canonical engine v2 evaluation for the Data slide-in panel. ──
  // Mirrors what `<InspectionRenderer>` computes internally so the panel
  // sees the same resolved state + rule trace as the rendered widgets.
  const allFormsArr = useMemo(() => {
    return [...forms.values()].map((f) => f as unknown as InspectionForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms]);
  const allResponsesFlat = useMemo(() => {
    const out: Record<string, FieldResponse> = {};
    const canonical = persistAdapter.getCanonicalResponses();
    for (const [fid, r] of Object.entries(canonical)) out[fid] = { value: r.value, isNA: r.isNA };
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistAdapter, allValues]);
  const resolvedStatesV2: Record<string, ResolvedFormStateV2> = useMemo(() => {
    try {
      return evaluateAllFormRulesWithTrace(
        allFormsArr,
        allResponsesFlat,
        (template.rules || []) as unknown as InspectionRule[],
      );
    } catch {
      return {};
    }
  }, [allFormsArr, allResponsesFlat, template.rules]);
  const rulesTraceV2: RuleTraceEntry[] = useMemo(() => {
    const out: RuleTraceEntry[] = [];
    const seen = new Set<string>();
    for (const fs of Object.values(resolvedStatesV2)) {
      for (const t of fs.trace ?? []) {
        if (seen.has(t.ruleId)) continue;
        seen.add(t.ruleId);
        out.push(t);
      }
    }
    return out;
  }, [resolvedStatesV2]);

  // ── Form navigation ──
  const sortedRefs = [...(template.formRefs ?? [])].sort((a, b) => a.order - b.order);
  const visibleRefs = showHiddenForms
    ? sortedRefs
    : sortedRefs.filter((ref) => !ruleResult.hiddenForms.has(ref.formId));

  const safeIndex = Math.min(currentFormIndex, Math.max(0, visibleRefs.length - 1));
  const currentRef = visibleRefs[safeIndex];
  const rawForm = currentRef ? forms.get(currentRef.formId) : null;
  // Repair missing formKind using FormRef if the Dexie record is missing it
  const currentForm = rawForm && !rawForm.formKind && currentRef?.formKind
    ? { ...rawForm, formKind: currentRef.formKind, moduleConfig: currentRef.moduleConfig }
    : rawForm;

  // ── Telemetry for Session Data panel ──
  const totalContactChanges = Object.values(contactChanges).reduce((sum, arr) => sum + arr.length, 0);
  const totalViolationChanges = Object.values(violationChanges).reduce((sum, arr) => sum + arr.length, 0);
  const telemetry = buildPreviewTelemetry({
    templateRules: (template.rules || []) as unknown as InspectionRule[],
    formRefs: template.formRefs ?? [],
    forms: forms as unknown as ReadonlyMap<string, InspectionForm>,
    allValues,
    loggedViolationCount: totalViolationChanges,
    contactChangeCount: totalContactChanges,
  });
  const totalActivity = telemetry.totalActivity;

  // Clamp form index when visible list shrinks
  useEffect(() => {
    if (currentFormIndex >= visibleRefs.length && visibleRefs.length > 0) {
      setCurrentFormIndex(visibleRefs.length - 1);
    }
  }, [visibleRefs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-open panel if user makes changes after closing
  useEffect(() => {
    if (totalActivity > 0 && !dataPanel) setDataPanel(true);
  }, [totalActivity]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleValuesChange = useCallback((_formId: string, _values: Record<string, any>) => {
    // Legacy callback retained for the dead `StandardFormRenderer` path. The
    // canonical `<InspectionRenderer>` writes through `persistAdapter` and the
    // `allValues` mirror re-derives on its `subscribe` notify.
  }, []);

  const [moduleContacts, setModuleContacts] = useState<ContactEntry[]>([]);
  const [resetCount, setResetCount] = useState(0);

  const handleReset = useCallback(() => {
    persistAdapter.reset();
    setResetCount((n) => n + 1);
    setContactChanges({});
    setViolationChanges({});
    setModuleContacts([]);
    setCurrentFormIndex(0);
    setDataPanel(true);
  }, [persistAdapter]);

  const canGoPrev = safeIndex > 0;
  const canGoNext = safeIndex < visibleRefs.length - 1;
  const hasRuleEffects = ruleResult.hiddenForms.size > 0 || ruleResult.hiddenFields.size > 0 || ruleResult.hiddenSections.size > 0;

  if (sortedRefs.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
        No forms in this template
      </div>
    );
  }

  // ── Per-form rule evaluation for standard forms ──
  // Evaluate which fields and sections are hidden for the current standard form
  const currentFormValues = (currentRef ? allValues[currentRef.formId] : {}) ?? {};
  let hiddenFieldsForForm = new Set<string>();
  let hiddenSectionsForForm = new Set<string>();
  if (currentForm && !['header', 'module'].includes(currentForm.formKind ?? '')) {
    for (const [fieldId, state] of Object.entries(ruleResult.hiddenFields ?? {})) {
      if (currentForm.fields.some((f) => f.id === fieldId)) hiddenFieldsForForm.add(fieldId);
    }
    for (const [sectionId] of Object.entries(ruleResult.hiddenSections ?? {})) {
      if (currentForm.layout?.sections?.some((s) => s.id === sectionId)) hiddenSectionsForForm.add(sectionId);
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-auto bg-gray-100">
      {/* Preview toolbar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3">
        {/* Device picker */}
        <div className="flex items-center gap-0 rounded-md border border-gray-200 overflow-hidden">
          {(['phone', 'tablet', 'desktop'] as DeviceWidth[]).map((d) => {
            const Icon = d === 'phone' ? Smartphone : d === 'tablet' ? Tablet : Monitor;
            return (
              <button
                key={d}
                onClick={() => setDevice(d)}
                className={`p-1.5 transition-colors ${device === d ? 'bg-[var(--brand-primary)] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                title={d.charAt(0).toUpperCase() + d.slice(1)}
              >
                <Icon size={14} />
              </button>
            );
          })}
        </div>

        <div className="h-4 w-px bg-gray-200" />

        {/* Form navigation */}
        <div className="flex items-center gap-1.5">
          <button
            title="Previous form"
            onClick={() => setCurrentFormIndex((i) => Math.max(0, i - 1))}
            disabled={!canGoPrev}
            className="p-1 rounded text-gray-500 hover:text-gray-800 disabled:opacity-30 hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-gray-600 font-medium min-w-[80px] text-center">
            {currentForm?.name || '—'}
          </span>
          <button
            title="Next form"
            onClick={() => setCurrentFormIndex((i) => Math.min(visibleRefs.length - 1, i + 1))}
            disabled={!canGoNext}
            className="p-1 rounded text-gray-500 hover:text-gray-800 disabled:opacity-30 hover:bg-gray-100 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Step dots */}
        <div className="flex items-center gap-1">
          {visibleRefs.map((ref, i) => (
            <button
              key={ref.formId}
              onClick={() => setCurrentFormIndex(i)}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === safeIndex ? 'bg-[var(--brand-primary)]' : 'bg-gray-300 hover:bg-gray-400'
              }`}
              title={forms.get(ref.formId)?.name}
            />
          ))}
        </div>

        <div className="flex-1" />

        {/* Show hidden forms toggle */}
        {ruleResult.hiddenForms.size > 0 && (
          <button
            onClick={() => setShowHiddenForms((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded transition-colors hover:bg-gray-50 text-gray-600"
          >
            {showHiddenForms ? <EyeOff size={12} /> : <EyeOff size={12} />}
            {ruleResult.hiddenForms.size} hidden
          </button>
        )}
      </div>

      {/* Main content area: form nav sidebar | form | session data */}
      <div ref={layoutContainerRef} className="flex-1 flex min-h-0">
        {/* Form nav list */}
        <div className="w-48 shrink-0 border-r bg-white overflow-y-auto py-2">
          {sortedRefs.map((ref) => {
            const form = forms.get(ref.formId);
            const isHidden = ruleResult.hiddenForms.has(ref.formId);
            const visibleIndex = visibleRefs.findIndex((r) => r.formId === ref.formId);
            const isActive = visibleIndex !== -1 && visibleIndex === currentFormIndex;

            return (
              <button
                key={ref.formId}
                onClick={() => {
                  if (!isHidden || showHiddenForms) {
                    const idx = visibleRefs.findIndex((r) => r.formId === ref.formId);
                    if (idx !== -1) setCurrentFormIndex(idx);
                  }
                }}
                disabled={isHidden && !showHiddenForms}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                  isActive
                    ? 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] font-medium border-r-2 border-[var(--brand-primary)]'
                    : isHidden
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-[10px] font-bold shrink-0">
                  {ref.order}
                </span>
                <span className="flex-1 truncate">{form?.name || ref.formId}</span>
                {isHidden && <EyeOff size={10} className="text-gray-300 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Preview card */}
        <div className="flex-1 overflow-auto flex justify-center py-6 px-8">
          {currentForm ? (
            <div
              className="bg-white rounded-2xl shadow-lg transition-all duration-300 flex flex-col self-start shrink-0"
              style={
                currentForm.formKind === 'module'
                  ? device === 'desktop'
                    ? { width: '100%', maxWidth: 1200, overflow: 'visible' }
                    : { width: DEVICE_WIDTHS[device], maxWidth: '100%', overflow: 'visible' }
                  : device === 'desktop'
                    ? { width: '100%', maxWidth: 1200, overflow: 'hidden' }
                    : { width: DEVICE_WIDTHS[device], maxWidth: '100%', overflow: 'hidden' }
              }
            >
              {/* Phone chrome */}
              {device === 'phone' && (
                <div className="bg-gray-900 px-4 py-1.5 flex items-center justify-between shrink-0">
                  <span className="text-white text-[10px]">9:41 AM</span>
                  <div className="flex items-center gap-1">
                    <div className="w-1 h-1 rounded-full bg-white/60" />
                    <div className="w-1 h-1 rounded-full bg-white/60" />
                    <div className="w-1 h-1 rounded-full bg-white" />
                  </div>
                </div>
              )}

              {/* Form content — canonical unified renderer (B18 cutover). */}
              <div>
                {currentForm.formKind === 'header' ? (
                  // Header forms remain on HeaderFormRenderer for TipTap parity.
                  <HeaderFormRenderer html={currentForm.description} />
                ) : currentForm.formKind === 'module' && (currentForm.moduleConfig as ContactsModuleConfig | undefined)?.moduleType === 'contacts' ? (
                  <ContactsModulePreview
                    config={(currentForm.moduleConfig as ContactsModuleConfig) ?? { moduleType: 'contacts', roles: [], allowAddNew: true, allowSelectExisting: true }}
                    initialContacts={moduleContacts}
                    onContactsChange={setModuleContacts}
                    mode="session"
                  />
                ) : (
                  <InspectionRenderer
                    mode="preview"
                    forms={[currentForm as unknown as InspectionForm]}
                    templateRules={(template.rules || []) as unknown as InspectionRule[]}
                    persistAdapter={persistAdapter}
                    attachmentPicker={attachmentPicker}
                    resolveContext={resolveContext}
                    caseId={`preview-${template._id ?? 'tmp'}`}
                    inspectorId="preview-inspector"
                    templateName={template.name}
                    currentEventIndex={0}
                    density={{ density: device === 'phone' ? 'mobile' : 'desktop' }}
                    resetKey={resetCount}
                  />
                )}
              </div>

              {/* Phone navigation footer */}
              {device === 'phone' && (
                <div className="border-t bg-white px-4 py-3 flex gap-2 shrink-0">
                  <button
                    onClick={() => setCurrentFormIndex((i) => Math.max(0, i - 1))}
                    disabled={!canGoPrev}
                    className="flex-1 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 disabled:opacity-30 hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentFormIndex((i) => Math.min(visibleRefs.length - 1, i + 1))}
                    disabled={!canGoNext}
                    className="flex-1 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-30 transition-colors bg-[var(--brand-primary)]"
                  >
                    {canGoNext ? 'Next' : 'Finish'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center text-sm text-gray-400">
              Select a form to preview
            </div>
          )}
        </div>

        {/* Session Data panel */}
        <div className="flex shrink-0">
          {dataPanel && (
            <div
              className="shrink-0 border-l bg-white flex flex-row overflow-hidden"
              style={{ width: dataPanelWidth }}
            >
              {/* Resize handle */}
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize session data panel"
                aria-valuemin={SESSION_PANEL_MIN_WIDTH}
                aria-valuenow={dataPanelWidth}
                tabIndex={0}
                onPointerDown={handleResizePointerDown}
                onKeyDown={handleResizeKeyDown}
                className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-blue-200 active:bg-blue-300 focus:outline-none focus:bg-blue-300 transition-colors"
                title="Drag to resize — or use ← / → when focused"
              />
              <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 shrink-0">
                  <span className="text-xs font-semibold text-gray-600">
                    Session Data
                    {totalActivity > 0 && <span className="ml-1.5 text-[10px] text-blue-500 font-bold">{totalActivity}</span>}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleReset}
                      title="Clear all entered data and reset session"
                      className="px-1.5 py-0.5 text-[10px] font-medium text-red-500 border border-red-200 rounded hover:bg-red-50 transition-colors"
                    >
                      Reset
                    </button>
                    <button
                      title="Close session data panel"
                      onClick={() => setDataPanel(false)}
                      className="p-0.5 rounded text-gray-400 hover:text-gray-700 transition-colors"
                    >
                      <XIcon size={13} />
                    </button>
                  </div>
                </div>
                {/* Canonical Data slide-in — Human + Raw Collection tabs (B14 + G-023). */}
                <div className="flex-1 overflow-hidden">
                  <DataSlideIn
                    mode="preview"
                    caseId={`preview-${template._id ?? 'tmp'}`}
                    inspectorId="preview-inspector"
                    templateName={template.name}
                    currentEventIndex={0}
                    openedAt={openedAtRef.current}
                    forms={allFormsArr}
                    resolvedStates={resolvedStatesV2}
                    rulesTrace={rulesTraceV2}
                    responses={persistAdapter.getCanonicalResponses()}
                    violations={persistAdapter.getViolations()}
                    contactChanges={persistAdapter.getContactChanges()}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab strip */}
          <div className="w-8 flex flex-col items-center pt-3 bg-[#f3f4f6] border-l border-gray-200 shrink-0">
            <button
              onClick={() => setDataPanel((v) => !v)}
              title="Session Data"
              className={`w-7 flex flex-col items-center gap-0.5 py-2 rounded transition-colors ${
                dataPanel
                  ? 'bg-white text-blue-600 shadow-sm ring-1 ring-gray-200'
                  : 'text-gray-400 hover:text-gray-700 hover:bg-white'
              }`}
            >
              <ClipboardList size={13} />
              {totalActivity > 0 && (
                <span className="text-[8px] font-bold text-blue-500 leading-none">{totalActivity}</span>
              )}
              <span className="text-[8px] font-semibold tracking-wide leading-none mt-0.5 [writing-mode:vertical-rl] rotate-180">
                Session
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
